import React, { useEffect, useMemo, useState } from 'react';
import adminApi from '../../services/adminApi';
import { MediaService } from '../../services/mediaService';
import styles from './AdminCuratedLists.module.css';

const LIST_TYPES = [
    'starter_pack',
    'essentials',
    'before_you_watch',
    'weekend_picks',
    'director_spotlight',
    'genre_guide',
    'era_guide',
    'staff_picks',
    'other',
];

const EMPTY_FORM = {
    title: '',
    slug: '',
    description: '',
    long_description: '',
    cover_image_url: '',
    cover_image_source: null,
    list_type: 'starter_pack',
    category: '',
    tags: '',
    is_featured: true,
    is_active: false,
    sort_order: 0,
};

const curatedItemKey = (item) => `${item.mediaType}:${item.mediaId}:${item.seasonNumber == null ? 'series' : item.seasonNumber}`;

const getItemTitle = (item, compact = false) => {
    if (compact && item.shortDisplayTitle) return item.shortDisplayTitle;
    return item.displayTitle || (item.seasonNumber != null ? `${item.title} — Season ${item.seasonNumber}` : item.title);
};

function Preview({ list }) {
    if (!list) return null;
    const previewUrl = list.previewCoverUrl || list.coverImageUrl;
    return (
        <div className={styles.preview}>
            <div className={styles.previewCover}>
                {previewUrl ? <img src={previewUrl} alt={`${list.title || 'Curated list'} cover`} /> : <span>Aluvo guide cover</span>}
            </div>
            <div>
                <span className={styles.type}>{list.listType?.replaceAll('_', ' ')}</span>
                <h2>{list.title}</h2>
                <p>{list.description}</p>
                <ol>
                    {(list.items || []).map((item) => (
                        <li key={item.id}>
                            <strong>{getItemTitle(item)}</strong>
                            <span>{item.mediaType === 'tv' ? 'TV' : 'Movie'}{item.year ? ` · ${item.year}` : ''}</span>
                            {item.editorialNote && <em>{item.editorialNote}</em>}
                        </li>
                    ))}
                </ol>
            </div>
        </div>
    );
}

export default function AdminCuratedLists() {
    const [lists, setLists] = useState([]);
    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [search, setSearch] = useState('');
    const [results, setResults] = useState([]);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const [coverFile, setCoverFile] = useState(null);
    const [coverPreviewUrl, setCoverPreviewUrl] = useState(null);
    const [seasonChoices, setSeasonChoices] = useState({});
    const [seasonLoadingKey, setSeasonLoadingKey] = useState(null);

    const selectedItemIds = useMemo(() => new Set((selected?.items || []).map(curatedItemKey)), [selected]);

    async function loadLists() {
        const data = await adminApi.listCuratedLists();
        setLists(data.curatedLists || []);
    }

    async function loadDetail(id) {
        const data = await adminApi.getCuratedList(id);
        const list = data.curatedList;
        setSelected(list);
        setForm({
            title: list.title || '',
            slug: list.slug || '',
            description: list.description || '',
            long_description: list.longDescription || '',
            cover_image_url: list.coverImageUrl || '',
            cover_image_source: list.coverImageSource || (list.coverImageUrl ? 'url' : null),
            list_type: list.listType || 'other',
            category: list.category || '',
            tags: (list.tags || []).join(', '),
            is_featured: !!list.isFeatured,
            is_active: !!list.isActive,
            sort_order: list.sortOrder || 0,
        });
    }

    useEffect(() => {
        loadLists().catch((err) => setMessage(err.message));
    }, []);

    useEffect(() => () => {
        if (coverPreviewUrl) URL.revokeObjectURL(coverPreviewUrl);
    }, [coverPreviewUrl]);

    useEffect(() => {
        if (!search.trim()) {
            setResults([]);
            return;
        }
        const timer = setTimeout(() => {
            adminApi.searchProviderMedia({ q: search, page: 1 })
                .then((data) => setResults(data.results || data.media || []))
                .catch(() => setResults([]));
        }, 350);
        return () => clearTimeout(timer);
    }, [search]);

    function setField(key, value) {
        setForm((prev) => ({ ...prev, [key]: value }));
    }

    function selectCoverFile(event) {
        const file = event.target.files?.[0];
        if (!file) return;
        if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
            setMessage('Cover image must be a JPEG, PNG, or WebP file.');
            event.target.value = '';
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            setMessage('Cover image must be 10 MB or smaller.');
            event.target.value = '';
            return;
        }
        if (coverPreviewUrl) URL.revokeObjectURL(coverPreviewUrl);
        setCoverFile(file);
        setCoverPreviewUrl(URL.createObjectURL(file));
        setField('cover_image_source', 'upload');
        setMessage(null);
    }

    function clearPendingCover() {
        if (coverPreviewUrl) URL.revokeObjectURL(coverPreviewUrl);
        setCoverFile(null);
        setCoverPreviewUrl(null);
    }

    function removeCover() {
        clearPendingCover();
        setForm((prev) => ({ ...prev, cover_image_url: '', cover_image_source: null }));
    }

    async function saveList(event) {
        event.preventDefault();
        setSaving(true);
        setMessage(null);
        try {
            const payload = {
                ...form,
                sort_order: Number(form.sort_order) || 0,
                tags: form.tags,
            };
            if (coverFile || form.cover_image_source === 'upload') {
                delete payload.cover_image_url;
            }
            const data = selected
                ? await adminApi.updateCuratedList(selected.id, payload)
                : await adminApi.createCuratedList(payload);
            const savedList = coverFile
                ? (await adminApi.uploadCuratedListCover(data.curatedList.id, coverFile)).curatedList
                : data.curatedList;
            clearPendingCover();
            await loadLists();
            await loadDetail(savedList.id);
            setMessage('Curated list saved.');
        } catch (err) {
            setMessage(err.message || 'Save failed.');
        } finally {
            setSaving(false);
        }
    }

    async function addMedia(item) {
        if (!selected) return;
        const mediaId = item.tmdbId ?? item.tmdb_id;
        const mediaType = item.kind;
        const seasonNumber = item.seasonNumber ?? null;
        if (selectedItemIds.has(`${mediaType}:${mediaId}:${seasonNumber == null ? 'series' : seasonNumber}`)) {
            setMessage('That title and season target is already in this list.');
            return;
        }
        await adminApi.addCuratedListItem(selected.id, { media_id: mediaId, media_type: mediaType, season_number: seasonNumber });
        await loadDetail(selected.id);
        setSearch('');
        setResults([]);
    }

    async function loadSeasons(item) {
        const mediaId = item.tmdbId ?? item.tmdb_id;
        const key = `tv:${mediaId}`;
        if (seasonChoices[key]) return;
        setSeasonLoadingKey(key);
        try {
            const seasons = await MediaService.getTvSeasons(mediaId);
            setSeasonChoices((prev) => ({
                ...prev,
                [key]: Array.isArray(seasons) ? seasons.filter((season) => season.seasonNumber > 0) : [],
            }));
        } catch (err) {
            setMessage(err.message || 'Could not load seasons for that TV show.');
        } finally {
            setSeasonLoadingKey(null);
        }
    }

    async function toggleSeasonPicker(item) {
        const mediaId = item.tmdbId ?? item.tmdb_id;
        await loadSeasons(item);
        setResults((prev) => prev.map((result) => {
            const resultId = result.tmdbId ?? result.tmdb_id;
            if (result.kind === 'tv' && resultId === mediaId) return { ...result, showSeasons: !result.showSeasons };
            return result;
        }));
    }

    async function moveItem(itemId, direction) {
        const items = [...(selected?.items || [])];
        const index = items.findIndex((item) => item.id === itemId);
        const nextIndex = index + direction;
        if (index < 0 || nextIndex < 0 || nextIndex >= items.length) return;
        [items[index], items[nextIndex]] = [items[nextIndex], items[index]];
        await adminApi.reorderCuratedListItems(selected.id, items.map((item) => item.id));
        await loadDetail(selected.id);
    }

    async function saveItem(item, updates) {
        await adminApi.updateCuratedListItem(selected.id, item.id, updates);
        await loadDetail(selected.id);
    }

    async function removeItem(itemId) {
        if (!window.confirm('Remove this title from the curated list?')) return;
        await adminApi.removeCuratedListItem(selected.id, itemId);
        await loadDetail(selected.id);
    }

    async function archiveList() {
        if (!selected || !window.confirm('Archive this curated list?')) return;
        await adminApi.archiveCuratedList(selected.id);
        setSelected(null);
        setForm(EMPTY_FORM);
        clearPendingCover();
        await loadLists();
    }

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div>
                    <h1>Curated Lists</h1>
                    <p>Editorial watch paths for Discover, category pages, and media details.</p>
                </div>
                <button onClick={() => { setSelected(null); setForm(EMPTY_FORM); clearPendingCover(); }}>New List</button>
            </header>

            {message && <div className={styles.message}>{message}</div>}

            <div className={styles.layout}>
                <aside className={styles.sidebar}>
                    {lists.map((list) => (
                        <button
                            key={list.id}
                            className={selected?.id === list.id ? styles.listActive : styles.listButton}
                            onClick={() => loadDetail(list.id)}
                        >
                            <strong>{list.title}</strong>
                            <span>{list.itemCount || 0} titles · {list.isActive ? 'Published' : 'Draft'}</span>
                        </button>
                    ))}
                </aside>

                <main className={styles.editor}>
                    <form onSubmit={saveList} className={styles.form}>
                        <label>Title<input value={form.title} onChange={(e) => setField('title', e.target.value)} required /></label>
                        <label>Slug<input value={form.slug} onChange={(e) => setField('slug', e.target.value)} placeholder="auto-generated if blank" /></label>
                        <label>Short Description<textarea value={form.description} onChange={(e) => setField('description', e.target.value)} required /></label>
                        <label>Long Description<textarea value={form.long_description} onChange={(e) => setField('long_description', e.target.value)} /></label>
                        <fieldset className={styles.coverFieldset}>
                            <legend>Cover Image</legend>
                            <div className={styles.coverControls}>
                                <label className={styles.uploadBox}>
                                    <span>Upload Cover Image <em>preferred</em></span>
                                    <input type="file" accept="image/jpeg,image/png,image/webp" onChange={selectCoverFile} />
                                </label>
                                {(coverPreviewUrl || form.cover_image_source === 'upload') && (
                                    <button type="button" onClick={removeCover}>Remove uploaded cover</button>
                                )}
                            </div>
                            <label>
                                Image URL <em>optional fallback</em>
                                <input
                                    value={form.cover_image_source === 'upload' ? '' : form.cover_image_url}
                                    onChange={(e) => {
                                        clearPendingCover();
                                        setForm((prev) => ({
                                            ...prev,
                                            cover_image_url: e.target.value,
                                            cover_image_source: e.target.value.trim() ? 'url' : null,
                                        }));
                                    }}
                                    disabled={form.cover_image_source === 'upload'}
                                    placeholder="https://..."
                                />
                            </label>
                            <p>Recommended artwork ratio is 4:5, around 1080 x 1350. The preview crops like Discover cards.</p>
                        </fieldset>
                        <div className={styles.grid2}>
                            <label>Type<select value={form.list_type} onChange={(e) => setField('list_type', e.target.value)}>{LIST_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}</select></label>
                            <label>Category<input value={form.category} onChange={(e) => setField('category', e.target.value)} placeholder="anime, horror, comedy" /></label>
                            <label>Tags<input value={form.tags} onChange={(e) => setField('tags', e.target.value)} placeholder="Anime, Starter Pack" /></label>
                            <label>Sort Order<input type="number" value={form.sort_order} onChange={(e) => setField('sort_order', e.target.value)} /></label>
                        </div>
                        <div className={styles.checks}>
                            <label><input type="checkbox" checked={form.is_featured} onChange={(e) => setField('is_featured', e.target.checked)} /> Featured</label>
                            <label><input type="checkbox" checked={form.is_active} onChange={(e) => setField('is_active', e.target.checked)} /> Published</label>
                        </div>
                        <div className={styles.actions}>
                            <button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save Draft'}</button>
                            {selected && <button type="button" onClick={() => setField('is_active', !form.is_active)}>{form.is_active ? 'Unpublish on Save' : 'Publish on Save'}</button>}
                            {selected && <button type="button" onClick={archiveList}>Archive</button>}
                        </div>
                    </form>

                    {selected && (
                        <section className={styles.items}>
                            <h2>Titles</h2>
                            <div className={styles.searchBox}>
                                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search movies or TV shows to add" />
                                {results.length > 0 && (
                                    <div className={styles.results}>
                                        {results.map((item) => {
                                            const mediaId = item.tmdbId ?? item.tmdb_id;
                                            const key = `${item.kind}:${mediaId}`;
                                            const seasons = seasonChoices[key] || [];
                                            return (
                                                <div key={key} className={styles.resultGroup}>
                                                    <button onClick={() => item.kind === 'tv' ? toggleSeasonPicker(item) : addMedia(item)}>
                                                        {item.posterUrl && <img src={item.posterUrl} alt="" />}
                                                        <span>{item.title} {item.year ? `(${item.year})` : ''}</span>
                                                        <em>{item.kind === 'tv' ? 'TV' : 'Movie'}</em>
                                                    </button>
                                                    {item.kind === 'tv' && item.showSeasons && (
                                                        <div className={styles.seasonOptions}>
                                                            <button onClick={() => addMedia({ ...item, seasonNumber: null })}>Entire Series</button>
                                                            {seasonLoadingKey === key && <span>Loading seasons…</span>}
                                                            {seasons.map((season) => (
                                                                <button
                                                                    key={season.seasonNumber}
                                                                    onClick={() => addMedia({ ...item, seasonNumber: season.seasonNumber })}
                                                                >
                                                                    Season {season.seasonNumber}{season.name && season.name !== `Season ${season.seasonNumber}` ? ` · ${season.name}` : ''}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {(selected.items || []).map((item) => (
                                <div key={item.id} className={styles.item}>
                                    {item.posterUrl ? <img src={item.posterUrl} alt="" /> : <div className={styles.posterFallback}>No poster</div>}
                                    <div>
                                        <strong>{item.position}. {getItemTitle(item)}</strong>
                                        <span>
                                            {item.mediaType === 'tv' ? 'TV' : 'Movie'} · TMDB {item.mediaId}
                                            {item.mediaType === 'tv' && item.seasonNumber == null ? ' · Entire Series' : ''}
                                        </span>
                                        {item.mediaType === 'tv' && (
                                            <label className={styles.itemSeason}>
                                                Season
                                                <select
                                                    value={item.seasonNumber ?? ''}
                                                    onFocus={() => loadSeasons({ kind: 'tv', tmdbId: item.mediaId })}
                                                    onChange={(e) => saveItem(item, { season_number: e.target.value === '' ? null : Number(e.target.value) })}
                                                >
                                                    <option value="">Entire Series</option>
                                                    {(seasonChoices[`tv:${item.mediaId}`] || []).map((season) => (
                                                        <option key={season.seasonNumber} value={season.seasonNumber}>
                                                            Season {season.seasonNumber}{season.name && season.name !== `Season ${season.seasonNumber}` ? ` · ${season.name}` : ''}
                                                        </option>
                                                    ))}
                                                    {item.seasonNumber != null && !(seasonChoices[`tv:${item.mediaId}`] || []).some((season) => season.seasonNumber === item.seasonNumber) && (
                                                        <option value={item.seasonNumber}>Season {item.seasonNumber}</option>
                                                    )}
                                                </select>
                                            </label>
                                        )}
                                        <textarea
                                            defaultValue={item.editorialNote || ''}
                                            onBlur={(e) => saveItem(item, { editorial_note: e.target.value })}
                                            placeholder="Editorial note"
                                        />
                                    </div>
                                    <div className={styles.itemActions}>
                                        <button onClick={() => moveItem(item.id, -1)}>↑</button>
                                        <button onClick={() => moveItem(item.id, 1)}>↓</button>
                                        <button onClick={() => removeItem(item.id)}>Remove</button>
                                    </div>
                                </div>
                            ))}
                        </section>
                    )}

                    <Preview
                        list={{
                            ...(selected || {}),
                            ...form,
                            listType: form.list_type,
                            title: form.title || selected?.title || 'Untitled curated list',
                            description: form.description || selected?.description || '',
                            coverImageUrl: form.cover_image_url,
                            previewCoverUrl: coverPreviewUrl,
                            items: selected?.items || [],
                        }}
                    />
                </main>
            </div>
        </div>
    );
}
